/**
 * Email Sidebar Controller — signal-store-backed folder/mailbox navigation.
 *
 * Delegates activeFolder/activeMailbox state to the central emailStore.
 * Turbo Stream sidebar count badge updates are handled server-side — this
 * controller only manages the active-link highlight and inline folder management.
 */

import { Controller } from '@hotwired/stimulus'
import { patch } from '@rails/request.js'
import { effect } from '@preact/signals'

export default class extends Controller {
  static targets = ['folder', 'folderItem', 'mailboxItem', 'unreadCount', 'newFolderForm', 'newFolderName']
  static values  = { activeFolder: String, activeMailbox: Number }

  connect() {
    this._disposers = []
    this._highlightActiveFromUrl()
  }

  disconnect() {
    this._disposers.forEach(d => d())
    this._disposers = []
  }

  // ── Folder / mailbox selection ────────────────────────────────────────────

  selectFolder(e) {
    const store = this._store()
    if (store) store.setActiveFolder(e.params.folder)
    this.setActiveLink(e.currentTarget)
  }

  selectMailbox(e) {
    const store = this._store()
    if (store) store.setActiveMailbox(e.params.mailbox)
    this.setActiveLink(e.currentTarget)
  }

  setActiveLink(activeEl) {
    this.folderItemTargets.forEach(item => {
      item.classList.toggle('active', item === activeEl)
    })
  }

  // Re-highlight the correct sidebar item after a Turbo Frame navigation.
  // Called via data-action="turbo:frame-load->email-sidebar#highlightActive".
  highlightActive() {
    this._highlightActiveFromUrl()
  }

  _highlightActiveFromUrl() {
    const currentSearch = window.location.search
    this.folderItemTargets.forEach(item => {
      try {
        const href    = item.getAttribute('href') || ''
        const itemUrl = new URL(href, window.location.origin)
        const isCurrent = itemUrl.pathname + itemUrl.search === window.location.pathname + currentSearch
        if (isCurrent) this.setActiveLink(item)
      } catch (_) { /* ignore malformed hrefs */ }
    })
  }

  // ── Inline new-folder form ────────────────────────────────────────────────

  showNewFolderInput(e) {
    e.preventDefault()
    if (this.hasNewFolderFormTarget) {
      this.newFolderFormTarget.classList.remove('d-none')
      this.newFolderNameTarget?.focus()
    }
  }

  hideNewFolderInput(e) {
    e?.preventDefault?.()
    if (this.hasNewFolderFormTarget) {
      this.newFolderFormTarget.classList.add('d-none')
      if (this.hasNewFolderNameTarget) this.newFolderNameTarget.value = ''
    }
  }

  createFolder(e) {
    if (!this.hasNewFolderNameTarget) return
    const name = this.newFolderNameTarget.value.trim()
    if (!name) {
      e.preventDefault()
      return
    }
    // Let the form submit naturally; hide the input after the next frame
    requestAnimationFrame(() => this.hideNewFolderInput())
  }

  async renameFolder(e) {
    e.preventDefault()
    const folderId   = e.currentTarget.dataset.folderId
    const currentName = e.currentTarget.dataset.folderName
    const newName    = prompt('', currentName)
    if (!newName || newName.trim() === '' || newName === currentName) return

    await patch(`/emails/folders/${folderId}`, {
      body: JSON.stringify({ email_folder: { name: newName.trim() } }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  // ── Helper ────────────────────────────────────────────────────────────────

  _store() {
    return window._emailStore || null
  }
}
